import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { Link } from "react-router-dom";
import { services } from "@/mocks/homeData";

export default function Services() {
  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <main>
        {/* Hero Header */}
        <section className="pt-28 md:pt-32 pb-12 md:pb-16 px-4 md:px-6 lg:px-10 bg-background-100">
          <div className="max-w-7xl mx-auto text-center">
            <span className="inline-block text-xs font-semibold uppercase tracking-wider text-accent-500 border border-accent-200 rounded-full px-3 py-1 mb-4">
              Our Services
            </span>
            <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-950 leading-tight mb-4">
              Fire Safety <span className="text-primary-500">Services</span>
            </h1>
            <p className="text-foreground-600 text-base md:text-lg max-w-2xl mx-auto">
              Comprehensive fire safety solutions for commercial, industrial, and residential properties across Singapore.
            </p>
          </div>
        </section>

        {/* Services Detail */}
        <section className="px-4 md:px-6 lg:px-10 py-12 md:py-16 max-w-7xl mx-auto">
          <div className="flex flex-col gap-12 md:gap-16">
            {services.map((service, index) => (
              <div
                key={service.id}
                className={`grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center ${
                  index % 2 === 1 ? "lg:grid-flow-dense" : ""
                }`}
              >
                <div className={`rounded-2xl overflow-hidden h-64 md:h-80 ${index % 2 === 1 ? "lg:col-start-2" : ""}`}>
                  <img
                    src={service.image}
                    alt={service.title}
                    title={`${service.title} - Ezzogenics`}
                    className="w-full h-full object-cover"
                    width="600"
                    height="400"
                    loading="lazy"
                  />
                </div>
                <div className={index % 2 === 1 ? "lg:col-start-1 lg:row-start-1" : ""}>
                  <div className="w-12 h-12 rounded-full bg-primary-100 flex items-center justify-center mb-4">
                    <i className={`${service.icon} text-primary-600 text-xl`}></i>
                  </div>
                  <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
                    {service.title}
                  </h2>
                  <p className="text-foreground-600 text-base leading-relaxed mb-4">
                    {service.description}
                  </p>
                  <p className="text-foreground-600 text-base leading-relaxed mb-6">
                    Our SCDF-certified engineers deliver end-to-end solutions including site assessment, system design, professional installation, and ongoing maintenance. All work complies with Singapore Standard SS CP 10 and SCDF regulations.
                  </p>
                  <Link
                    to="/contact"
                    className="inline-flex items-center gap-2 text-primary-500 font-semibold text-sm hover:text-primary-600 transition-colors underline underline-offset-4"
                  >
                    Request a quote
                    <i className="ri-arrow-right-line text-base"></i>
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Products Link Banner */}
        <section className="px-4 md:px-6 lg:px-10 py-8 max-w-7xl mx-auto">
          <div className="bg-accent-50 border border-accent-200/70 rounded-2xl p-6 flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 flex items-center justify-center rounded-full bg-accent-100 flex-shrink-0">
                <i className="ri-fire-line text-accent-600 text-xl"></i>
              </div>
              <div>
                <h3 className="font-heading text-lg font-bold text-foreground-950">
                  Browse Our Fire Safety Products
                </h3>
                <p className="text-foreground-600 text-sm">
                  Explore our full range of fire safety equipment including extinguishers, riser systems, alarm panels and more.
                </p>
              </div>
            </div>
            <Link
              to="/products"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-accent-500 text-background-50 text-sm font-semibold hover:bg-accent-600 transition-colors whitespace-nowrap flex-shrink-0"
            >
              View All Products
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </section>

        {/* Fire Prevention Services Overview */}
        <section className="bg-background-100 py-12 md:py-16">
          <div className="px-4 md:px-6 lg:px-10 max-w-7xl mx-auto">
            <div className="text-center mb-10">
              <span className="inline-block text-xs font-semibold uppercase tracking-wider text-accent-500 border border-accent-200 rounded-full px-3 py-1 mb-3">
                Fire Prevention
              </span>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
                Key Services for <span className="text-primary-500">Fire Prevention</span> in Singapore
              </h2>
              <p className="text-foreground-600 text-base max-w-2xl mx-auto">
                One of the greatest challenges of dealing with fire incidences is that nobody knows when they can occur. That only means you should always have the best measures in place to minimize the impacts whenever they occur.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {[
                {
                  icon: "ri-user-star-line",
                  title: "QP Consultancy Services",
                  desc: "There are critical fire safety procedures that only Qualified Persons should handle in Singapore. A QP will advise you on the most suitable fire protection measures for your building and budget. Services include FSSD Submission, Fire Safety Certificate Application, Temporary Fire Permit Application, Emergency Response Plan Application, and Minor Addition & Alteration Works.",
                },
                {
                  icon: "ri-tools-line",
                  title: "Fire Systems Installation",
                  desc: "To ensure better fire prevention at home, in the office, business or industrial space, there are various fire systems that you should install on the premises. Hiring installation services for fire systems will ensure that the procedure is done correctly, safely and at your convenience. Attempting to install fire systems without the know-how and appropriate tools could easily cause accidents and severe damage.",
                },
                {
                  icon: "ri-search-2-line",
                  title: "Testing & Inspection Services",
                  desc: "Fire protection systems should be regularly inspected and tested to make sure that they are in the best condition for use during a fire emergency. Testing and inspection should be performed during installation as well as in regular schedules. Main systems that should be regularly inspected include emergency lights & exit signs, fire protection systems, emergency power supply units, and smoke extraction and control units.",
                },
                {
                  icon: "ri-settings-3-line",
                  title: "Maintenance, Servicing & Repair",
                  desc: "Depending on the applications and the environments where they are used, fire protection systems also experience malfunction. Faulty or worn out fire protection systems are a significant hazard that should be properly diagnosed, serviced or repaired as soon as they are detected. An expert will thoroughly diagnose the systems to determine all the issues and proceed to perform maintenance and repair according to the findings.",
                },
                {
                  icon: "ri-calendar-check-line",
                  title: "Preventive Maintenance Agreements",
                  desc: "With just a single Preventive Maintenance Agreement (PMA), we ensure a comprehensive routine inspection and maintenance of all the fire prevention systems and products on your building. Our team of licensed QPs, inspectors and engineers guarantee tailor-made fire prevention and safety solutions for residential developments, hospitals, schools, hotels, businesses and industrial companies.",
                },
                {
                  icon: "ri-group-line",
                  title: "Fire Safety Training",
                  desc: "Fire safety training should be offered to both old and new employees and reviewed regularly. The training focuses on implementing the results of fire risk assessment as well as fire safety codes. The management can also initiate occasional fire emergency drills to evaluate the effectiveness of the training. A professional fire safety consultant can provide the training at your convenience.",
                },
              ].map((item) => (
                <div key={item.title} className="bg-background-50 rounded-2xl p-6 border border-background-200/70">
                  <div className="w-11 h-11 flex items-center justify-center rounded-full bg-primary-100 mb-4">
                    <i className={`${item.icon} text-primary-600 text-xl`}></i>
                  </div>
                  <h3 className="font-semibold text-foreground-900 text-base mb-2">{item.title}</h3>
                  <p className="text-foreground-600 text-sm leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Commercial Fire Protection Services */}
        <section className="px-4 md:px-6 lg:px-10 py-12 md:py-16 max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 items-center">
            <div className="rounded-2xl overflow-hidden h-72 md:h-96">
              <img
                src="https://storage.readdy-site.link/project_files/bad53886-d0ad-4ee3-acb4-5b763a394fe2/cc0287f6-6225-4f3d-9056-6058667116e0_compressed_Depositphotos_76997131_l-2015.webp"
                alt="Commercial fire protection services Singapore"
                className="w-full h-full object-cover"
              />
            </div>
            <div>
              <span className="inline-block text-xs font-semibold uppercase tracking-wider text-accent-500 border border-accent-200 rounded-full px-3 py-1 mb-4">
                Commercial
              </span>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
                Commercial Fire <span className="text-primary-500">Protection Services</span>
              </h2>
              <p className="text-foreground-600 text-base leading-relaxed mb-4">
                According to the Fire Act, all commercial buildings should have efficient fire protection systems and products for fire safety. Commercial buildings include public spaces like shopping malls, retail stores, hotels and resorts, warehouses, data centres and recreational facilities.
              </p>
              <p className="text-foreground-600 text-base leading-relaxed mb-5">
                The main fire protection systems for commercial buildings include fire alarm &amp; detection systems, gas detection systems, fire sprinklers, fire suppression systems, fire protection systems, engineered smoke control systems, wet and dry rise systems. Key fire safety products include fire extinguishers, exit &amp; emergency lights, fire hydrants, fire pumps, fire rated doors, fire hose reels, fire alarm panel, fire blanket and smoke escape masks.
              </p>
              <div className="flex flex-wrap gap-2 mb-6">
                {["Saves Lives", "Protects Assets", "Minimizes Business Disruption", "Ensures Compliance"].map((b) => (
                  <span key={b} className="text-xs font-semibold bg-primary-100 text-primary-700 rounded-full px-3 py-1">{b}</span>
                ))}
              </div>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap"
              >
                Enquire for Commercial Buildings
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        </section>

        {/* Residential Fire Protection Services */}
        <section className="bg-background-100 py-12 md:py-16">
          <div className="px-4 md:px-6 lg:px-10 max-w-7xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 items-center">
              <div>
                <span className="inline-block text-xs font-semibold uppercase tracking-wider text-accent-500 border border-accent-200 rounded-full px-3 py-1 mb-4">
                  Residential
                </span>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
                  Residential Fire <span className="text-primary-500">Protection Services</span>
                </h2>
                <p className="text-foreground-600 text-base leading-relaxed mb-4">
                  Home fires are usually small in magnitude and seem less-risky. However, fire is a destructive incident that always requires immediate detection and response. In just a few minutes, the small fire in your home could put the lives of your family in great danger and damage a significant part of your belongings.
                </p>
                <p className="text-foreground-600 text-base leading-relaxed mb-5">
                  Residential fire protection services cover a wide range of areas aimed at ensuring that the occupants of the building and your belongings are accorded the best fire protection. A residential fire protection services company can supply fire protection systems and products as well as install them in your home — including fire alarm &amp; detection systems, fire and gas detection systems, engineered smoke control systems, fire suppression systems, exit &amp; emergency lights, heat and smoke detectors, portable fire extinguishers, fire smoke control dampers, fire blankets and smoke escape masks.
                </p>
                <div className="flex flex-col gap-3 mb-6">
                  {[
                    { icon: "ri-heart-pulse-line", title: "Saves Lives", desc: "Immediate detection and response prevents accidents and injuries to family members." },
                    { icon: "ri-home-3-line", title: "Protects Your Belongings", desc: "Contain fires quickly before they spread to cause massive destruction on the property." },
                    { icon: "ri-award-line", title: "Ensures Compliance", desc: "Meet fire safety standards required for HDB and high-rise residential units." },
                  ].map((item) => (
                    <div key={item.title} className="flex items-start gap-3">
                      <div className="w-9 h-9 flex items-center justify-center rounded-full bg-accent-100 flex-shrink-0">
                        <i className={`${item.icon} text-accent-600 text-base`}></i>
                      </div>
                      <div>
                        <h4 className="font-semibold text-foreground-900 text-sm mb-0.5">{item.title}</h4>
                        <p className="text-foreground-600 text-sm">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap"
                >
                  Enquire for Residential Buildings
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
              <div className="rounded-2xl overflow-hidden h-72 md:h-96">
                <img
                  src="https://storage.readdy-site.link/project_files/bad53886-d0ad-4ee3-acb4-5b763a394fe2/612e6a14-3cfd-48d1-b291-0c171b604119_compressed_Depositphotos_57764119_l-2015.webp"
                  alt="Residential fire protection services Singapore"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Additional Services */}
        <section className="bg-background-50 py-12 md:py-16">
          <div className="px-4 md:px-6 lg:px-10 max-w-7xl mx-auto">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 text-center mb-10">
              Additional <span className="text-primary-500">Services</span>
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {[
                { title: "SCDF Compliance Consulting", desc: "Expert guidance for Fire Safety Certificate (FSC) applications and SCDF submissions." },
                { title: "Fire Safety Plan Review", desc: "Professional review of fire safety plan drawings and documentation for approval." },
                { title: "Annual Maintenance Contracts", desc: "Scheduled inspections and maintenance to keep your systems SCDF-compliant year-round." },
                { title: "Fire Safety Training", desc: "On-site fire safety training for building management and security teams." },
                { title: "Emergency Evacuation Planning", desc: "Custom evacuation plans and drills tailored to your building layout and occupancy." },
                { title: "Fire Safety Manager Services", desc: "Qualified Fire Safety Manager (FSM) services for buildings requiring statutory appointments." },
              ].map((item) => (
                <div key={item.title} className="bg-background-50 rounded-2xl p-6 border border-background-200/70">
                  <h3 className="font-semibold text-foreground-900 text-base mb-2">{item.title}</h3>
                  <p className="text-foreground-600 text-sm leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="px-4 md:px-6 lg:px-10 py-12 md:py-16 max-w-7xl mx-auto text-center">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
            Need a Custom Fire Safety Solution?
          </h2>
          <p className="text-foreground-600 text-base max-w-xl mx-auto mb-6">
            Every building is unique. Contact us for a tailored fire safety assessment and quote.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap"
          >
            Upload Documents
            <i className="ri-upload-cloud-2-line text-lg"></i>
          </Link>
        </section>
      </main>
      <Footer />
    </div>
  );
}